import { createGlobalStyle } from 'styled-components';
import { colors, radius, shadowM, space } from '@ardoq/design-tokens';
import { text2 } from '@ardoq/typography';

/**
 * How wide a tooltip may get before it wraps.
 *
 * The one number here that is a measurement rather than a token, carried over from the LESS rule.
 * It is also the single most load-bearing line: without it a long label renders as one unwrapped
 * strip across the whole window, which is how this regression was spotted.
 */
const MAX_WIDTH = '272px';

/**
 * Ardoq's styling for the tooltip yFiles draws itself.
 *
 * The graph views ask yFiles for a tooltip by assigning a string to `e.toolTip`
 * (`modernizedTooltipListener`), and yFiles renders it into its own element with the
 * `.yfiles-tooltip` class - white background, 1px solid black, 10pt, no wrapping. This is what
 * makes that element look like the rest of the product.
 *
 * ## Why this is global, in a codebase that is moving away from global styles
 *
 * Because the element is not in our tree. `toolTipInputMode.toolTipParentElement` is left `null`
 * everywhere except fullscreen, and yFiles documents `null` as "the tooltip is added to the
 * `document.body`" - which is also why the fullscreen handlers reassign it: a tooltip mounted on
 * `body` is not inside the fullscreened subtree.
 *
 * So a rule nested inside `GraphContainer` compiles to a descendant selector that can never match.
 * That is exactly what happened: ARD-31195 (#34016) moved `yfilesViews.less` into
 * component-scoped styles, dropped this rule as dead yFiles 2.x code - it is alive in 3.0.4,
 * `yfiles.css:305` - and a later attempt to restore it inside `GraphContainer` would have been a
 * no-op for the same reason.
 *
 * **Do not move this into a styled-component.** If the element it targets is ever parented into
 * the view, `toolTipParentElement` is the thing to change, and then this can move with it.
 */
export const YFilesTooltipGlobalStyle = createGlobalStyle`
  .yfiles-tooltip {
    ${text2}
    max-width: ${MAX_WIDTH};
    padding: ${space.medium};
    color: ${colors.textDefault};
    background: ${colors.surfaceDefault};
    border-width: 0;
    border-radius: ${radius.medium};
    box-shadow: ${shadowM};

    /* The label arrives with newlines in it - the listener joins wrapped lines with \\r\\n - and a
       single long word must break rather than widen the box past the max above. */
    white-space: pre-wrap;
    overflow-wrap: break-word;
  }
`;
